import React, { useState, useRef, useEffect } from 'react';
import { FiX } from 'react-icons/fi';

export interface CompanyUser {
  email: string;
  label: string;
}

interface RecipientPickerProps {
  companyUsers: CompanyUser[];
  companyDomain?: string;
  value: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-invalid'?: boolean;
}

const RecipientPicker: React.FC<RecipientPickerProps> = ({
  companyUsers,
  companyDomain,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select or add user',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [addError, setAddError] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const domainLower = (companyDomain ?? '').toLowerCase().replace(/^@/, '');
  const domainSuffix = domainLower ? `@${domainLower}` : '';
  const restrictDomain = Boolean(domainLower);

  const filteredUsers = inputValue.trim()
    ? companyUsers.filter(
        (u) =>
          u.email.toLowerCase().includes(inputValue.toLowerCase()) ||
          u.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : companyUsers;

  const availableUsers = filteredUsers.filter((u) => !value.includes(u.email));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setListOpen(false);
        setInputValue('');
        setAddError('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isKnownUser = (email: string) =>
    companyUsers.some((u) => u.email.toLowerCase() === email);

  const addRecipient = (email: string) => {
    const e = email.trim().toLowerCase();
    if (!e || value.includes(e)) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    if (restrictDomain && !e.endsWith(domainSuffix) && !isKnownUser(e)) return;
    onChange([...value, e]);
  };

  const removeRecipient = (email: string) => {
    onChange(value.filter((x) => x !== email));
  };

  const tryAddFromInput = () => {
    setAddError('');
    const raw = inputValue.trim();
    if (!raw) return;

    const el = raw.toLowerCase();

    const exactMatch = availableUsers.find(
      (u) => u.email.toLowerCase() === el || u.label.toLowerCase() === el
    );
    if (exactMatch) {
      addRecipient(exactMatch.email);
      setInputValue('');
      setListOpen(false);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(el)) {
      setAddError('Enter a valid email address');
      return;
    }
    if (restrictDomain && !el.endsWith(domainSuffix) && !isKnownUser(el)) {
      setAddError(`Only emails @${domainLower} are allowed`);
      return;
    }
    if (value.includes(el)) {
      setAddError('Already added');
      return;
    }
    onChange([...value, el]);
    setInputValue('');
    setListOpen(false);
  };

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      tryAddFromInput();
    }
  };

  return (
    <div className="recipient-picker">
      {!disabled && (
        <div ref={containerRef} className="recipient-picker-left">
          <div className="recipient-picker-input-wrap">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setAddError('');
                setListOpen(true);
              }}
              onFocus={() => setListOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="recipient-picker-input"
              autoComplete="off"
            />
            {listOpen && (
              <div className="recipient-picker-dropdown">
                {availableUsers.length > 0 ? (
                  availableUsers.slice(0, 16).map((u) => (
                    <button
                      type="button"
                      key={u.email}
                      className="recipient-picker-option"
                      onClick={() => {
                        addRecipient(u.email);
                        setInputValue('');
                        setListOpen(false);
                      }}
                    >
                      <span className="recipient-picker-option-name">{u.label}</span>
                      <span className="recipient-picker-option-email">{u.email}</span>
                    </button>
                  ))
                ) : (
                  <div className="recipient-picker-empty">
                    {filteredUsers.length === 0
                      ? 'No matches'
                      : 'All selected or no users left'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="recipient-picker-right">
        {!disabled && addError && (
          <div className="recipient-picker-add-error">{addError}</div>
        )}
        <div className="recipient-picker-chips">
          {value.map((email) => (
            <span key={email} className="recipient-picker-chip">
              {email}
              {!disabled && (
                <button
                  type="button"
                  className="recipient-picker-chip-remove"
                  onClick={() => removeRecipient(email)}
                  aria-label={`Remove ${email}`}
                >
                  <FiX size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
        {value.length === 0 && disabled && (
          <div className="recipient-picker-placeholder">{placeholder}</div>
        )}
      </div>
    </div>
  );
};

export default RecipientPicker;
